import React from 'react'
import Image from 'next/image'
import { Swiper, SwiperSlide } from 'swiper/react'
import { Autoplay } from 'swiper/modules'
import 'swiper/css/bundle'
import { Header2 } from '../../styles/Components'
import { gsColors } from '../../styles/Variables'
import ReviewCard1 from '../../../public/about-us/review_card_1.jpg'
import ReviewCard2 from '../../../public/about-us/review_card_2.jpg'
import ReviewCard3 from '../../../public/about-us/review_card_3.jpg'
import ReviewCard4 from '../../../public/about-us/review_card_4.jpg'
import { TestimonialsWrapper } from './styles'

const DEFAULT_REVIEWS = [
  {
    stars: 5,
    title: 'Service was amazing!',
    quote: 'The booking experience was smooth, and my artist was fantastic! She was so sweet and talented, and really nailed exactly what I wanted for my makeup.',
    name: 'Nia T.',
  },
  {
    stars: 5,
    title: 'Obsessed',
    quote: 'I love the blowouts so much. The perfect bombshell blowout, and all of the stylists are so professional and friendly.',
    name: 'Larissa G.',
  },
  {
    stars: 5,
    title: 'Lasted all night!',
    quote: 'My makeup stayed on till 4am and everyone complimented me all night. By far the best makeup experience I’ve had.',
    name: 'Jessica C.',
  },
  {
    stars: 5,
    title: 'Always so happy',
    quote: 'The best makeup I’ve ever had done in my life—and I go to a lot of events.',
    name: 'Sabina J.',
  },
  {
    stars: 5,
    title: 'Updo Perfection',
    quote: 'Very collaborative and she stopped at several points to consult me. This was the best updo service I’ve ever had.',
    name: 'Lynn M.',
  },
  {
    stars: 5,
    title: 'Styled to last',
    quote: 'My wedding hairstyle held up for the entire day and looked great! When scheduling my appointments, everyone was helpful and prompt in responding.',
    name: 'Elizabeth M.',
  },
  {
    stars: 5,
    title: 'Never Disappoints',
    quote: 'Their silk press service was absolutely the best! My stylist was amazing-professional, pleasant, and made my hair look flawless.',
    name: 'Jasmin M.',
  },
  {
    stars: 5,
    title: 'Picture-Perfect',
    quote: 'We had blowouts and makeup done for the bride and six others. The quality was outstanding—everyone looked flawless in person and in photos.',
    name: 'Shari P.',
  },
]

const DEFAULT_PHOTOS = [
  { src: ReviewCard1, alt: 'Three Glamsquad clients in evening gowns with party-ready hair and makeup holding champagne at a celebration' },
  { src: ReviewCard2, alt: 'Three Glamsquad clients with fresh makeup and curly styled hair taking a selfie together indoors' },
  { src: ReviewCard3, alt: 'Glamsquad client photographed from behind showing a sleek wavy high ponytail with gold hoop earrings' },
  { src: ReviewCard4, alt: 'Smiling Glamsquad client relaxing with coffee while pros style her hair and paint her nails at home' },
]

// Pattern: every complete pair of 2 reviews is followed by 1 photo (photos cycle). A trailing
// unpaired review renders with no following photo.
export const buildTestimonials = (reviews, images) => {
  const list = []
  let imgIdx = 0
  for (let i = 0; i < reviews.length; i += 2) {
    list.push({ id: `review-${i}`, type: 'review', ...reviews[i] })
    if (reviews[i + 1]) {
      list.push({ id: `review-${i + 1}`, type: 'review', ...reviews[i + 1] })
      if (images.length) {
        list.push({ id: `photo-${imgIdx}`, type: 'photo', ...images[imgIdx % images.length] })
        imgIdx += 1
      }
    }
  }
  return list
}

const StarIcon = () => (
  <svg width="32" height="32" viewBox="0 0 24 24" fill={gsColors.salmon} xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">
    <path d="M12 2l2.9 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l7.1-1.01L12 2z" />
  </svg>
)

const ReviewSlide = ({ stars, title, quote, name }) => (
  <div className="review-card">
    <div className="review-stars" role="img" aria-label={`${stars} out of 5 stars`}>
      {new Array(stars).fill('').map((_, i) => (
        <StarIcon key={i} />
      ))}
    </div>
    <div className="review-text">
      {title ? <p className="review-title">{title}</p> : null}
      <p className="review-quote">{quote}</p>
      <p className="review-name">— {name}</p>
    </div>
  </div>
)

const PhotoSlide = ({ src, alt }) => (
  <div className="photo-card">
    <Image src={src} alt={alt} fill quality={80} style={{ objectFit: 'cover', objectPosition: 'center 30%' }} sizes="(max-width: 768px) calc(100vw - 40px), 411px" />
  </div>
)

// titleAlign / bgColor let a host page (e.g. HomePage2026) restyle the shared
// carousel without forking it. Defaults preserve the about-us page (centered
// heading, white background). showPhotos=false yields a reviews-only carousel
// (no interleaved photos), used by the service detail page.
const TestimonialsCarousel = ({ titleAlign = 'center', bgColor, reviews, images, showPhotos = true }) => {
  const source = reviews && reviews.length ? reviews : DEFAULT_REVIEWS
  const imgs = showPhotos ? (images && images.length ? images : DEFAULT_PHOTOS) : []
  const testimonials = buildTestimonials(source, imgs)
  const enableLoop = testimonials.length >= 4

  return (
    <TestimonialsWrapper aria-label="Customer reviews" bgColor={bgColor}>
      <Header2 className="carousel-heading" align={titleAlign} lineHeight="1.2" fontSize={32}>
        Over 200K happy customers<br />
        (and still counting)
      </Header2>
      <div className="carousel-container">
        <Swiper
          modules={[Autoplay]}
          slidesPerView="auto"
          spaceBetween={20}
          loop={enableLoop}
          autoplay={{ delay: 4500, disableOnInteraction: false }}
        >
          {testimonials.map((t) => (
            <SwiperSlide key={t.id} className="carousel-slide" style={{ width: 411, height: 460 }}>
              {t.type === 'review'
                ? <ReviewSlide stars={t.stars} title={t.title} quote={t.quote} name={t.name} />
                : <PhotoSlide src={t.src} alt={t.alt} />
              }
            </SwiperSlide>
          ))}
        </Swiper>
      </div>
    </TestimonialsWrapper>
  )
}

export default TestimonialsCarousel
